import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { pget, API, auth, fmtStamp } from "./portalApi";
import { FileText, Download, Folder, FolderOpen, Files, Search } from "lucide-react";

// Read-only mirror of the manager's document library: same folders, same organization,
// no uploads. Board-only folders never reach this endpoint.
function FolderRow({ label, icon: Icon, count, activeState, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold text-left transition-colors ${
        activeState ? "bg-ink text-white" : "text-ink-700 hover:bg-sand-50"}`}>
      <Icon className={`h-4 w-4 shrink-0 ${activeState ? "text-white" : "text-redline-500"}`} strokeWidth={1.75} />
      <span className="truncate flex-1">{label}</span>
      <span className={`text-[11px] rounded-full px-1.5 py-0.5 shrink-0 ${activeState ? "bg-white/20" : "bg-sand-100 text-ink-500"}`}>{count}</span>
    </button>
  );
}

export default function PortalDocuments() {
  const [searchParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [active, setActive] = useState("all"); // all | unfiled | <folderId>
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    pget("/resident/documents")
      .then((r) => {
        setFolders(r.data.folders || []);
        setItems(r.data.items || []);
        setUnfiledCount(r.data.unfiled_count || 0);
        // Deep-link from the home shelves: ?folder=<id> | unfiled opens that folder directly.
        const f = searchParams.get("folder");
        if (f && (f === "unfiled" || (r.data.folders || []).some((x) => x.id === f))) setActive(f);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const folderName = (id) => folders.find((f) => f.id === id)?.name || "";

  const scoped = useMemo(() => {
    let list = items;
    if (active === "unfiled") list = list.filter((d) => !d.folder_id);
    else if (active !== "all") list = list.filter((d) => d.folder_id === active);
    const s = query.trim().toLowerCase();
    if (s) list = list.filter((d) => (d.title || "").toLowerCase().includes(s) || (d.description || "").toLowerCase().includes(s));
    return list;
  }, [items, active, query]);

  const download = async (d) => {
    setBusy(d.id);
    try {
      const r = await axios.get(`${API}/resident/documents/${d.id}/download`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || `${d.title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download that file"); }
    finally { setBusy(""); }
  };

  const activeLabel = active === "all" ? "All documents" : active === "unfiled" ? "Other documents" : folderName(active);

  return (
    <div className="space-y-6" data-testid="portal-documents-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><FileText className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Documents</h1>
        <p className="text-muted-foreground mt-1">Your community&apos;s official documents, organized by your HOA. Pro tip: the concierge has read every one — just text your question.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground" data-testid="portal-documents-empty">No documents shared yet.</div>
      ) : (
        <div className="grid lg:grid-cols-[232px_1fr] gap-5 items-start">
          {/* Folder sidebar — inherited from the manager's library */}
          <aside className="rounded-2xl border border-sand-200 bg-white p-2.5 lg:sticky lg:top-20" data-testid="portal-folder-sidebar">
            <FolderRow label="All documents" icon={Files} count={items.length} activeState={active === "all"}
              onClick={() => setActive("all")} testid="portal-folder-all" />
            {folders.length > 0 && <div className="my-1.5 border-t border-sand-100" />}
            <div className="max-h-[420px] overflow-y-auto pr-0.5 space-y-0.5">
              {folders.map((f) => (
                <FolderRow key={f.id} label={f.name} icon={Folder} count={f.doc_count} activeState={active === f.id}
                  onClick={() => setActive(f.id)} testid={`portal-folder-${f.id}`} />
              ))}
            </div>
            {unfiledCount > 0 && (
              <>
                <div className="my-1.5 border-t border-sand-100" />
                <FolderRow label="Other documents" icon={FolderOpen} count={unfiledCount} activeState={active === "unfiled"}
                  onClick={() => setActive("unfiled")} testid="portal-folder-unfiled" />
              </>
            )}
          </aside>

          <div className="min-w-0 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-heading font-bold text-ink flex items-center gap-1.5 text-sm">
                <Folder className="h-4 w-4 text-redline-500" strokeWidth={1.75} /> {activeLabel}
              </span>
              <div className="relative ml-auto">
                <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="portal-documents-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search documents…"
                  className="rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink w-52" />
              </div>
            </div>

            {scoped.length === 0 ? (
              <div className="bg-white rounded-2xl border border-sand-200 p-10 text-center text-muted-foreground text-sm" data-testid="portal-documents-none">
                No documents {query ? "match your search" : "in this folder yet"}.
              </div>
            ) : (
              <div className="space-y-2" data-testid="portal-documents-list">
                {scoped.map((d) => (
                  <div key={d.id} data-testid={`portal-doc-${d.id}`} className="bg-white rounded-2xl border border-sand-200 p-4 flex items-center gap-3">
                    <span className="h-10 w-10 rounded-xl bg-sand-100 text-ink-600 flex items-center justify-center shrink-0"><FileText className="h-5 w-5" strokeWidth={1.5} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-ink truncate flex items-center gap-2">{d.title}
                        {d.historical && <span data-testid={`portal-doc-past-${d.id}`} className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold shrink-0">Past · expired {d.expires_at}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {active === "all" && d.folder_id && <span className="text-redline-600 font-semibold">{folderName(d.folder_id)} · </span>}
                        Added {fmtStamp(d.created_at)}
                      </div>
                    </div>
                    {d.downloadable && (
                      <button data-testid={`portal-doc-download-${d.id}`} disabled={busy === d.id} onClick={() => download(d)}
                        className="flex items-center gap-1.5 rounded-full border border-sand-200 px-3.5 py-2 text-sm font-semibold text-ink hover:border-ink transition-colors disabled:opacity-60 shrink-0">
                        <Download className="h-4 w-4" /> {busy === d.id ? "…" : "Download"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
